/** Reusable page-level components built on the UI primitives. */
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { AlertTriangle, Inbox, Loader2 } from "lucide-react";
import type { ReactElement, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { errorMessage } from "@/api/problem";
import { cn } from "@/shared/lib/format";
import { Badge, type badgeVariants } from "@/shared/ui/primitives";
import { Button } from "@/shared/ui/button";

import type { VariantProps } from "class-variance-authority";

type Tone = NonNullable<VariantProps<typeof badgeVariants>["tone"]>;

/** Map any domain status/priority string to a badge tone. */
export function toneFor(value: string): Tone {
  switch (value) {
    case "active":
    case "valid":
    case "verified":
    case "closed":
    case "resolved":
    case "completed":
    case "low":
      return "success";
    case "standby":
    case "on_hold":
    case "acknowledged":
    case "in_review":
    case "due":
    case "expiring":
    case "high":
    case "urgent":
    case "medium":
      return "warning";
    case "out_of_service":
    case "overdue":
    case "expired":
    case "emergency":
    case "critical":
    case "rejected":
    case "revoked":
      return "danger";
    case "in_progress":
    case "scheduled":
    case "approved":
    case "new":
    case "planned":
    case "converted":
    case "generated":
    case "under_maintenance":
      return "info";
    default:
      return "neutral";
  }
}

/** Badge that translates a status/priority key within a namespace. */
export function StatusBadge({
  value,
  namespace,
  className,
}: {
  value: string;
  /** i18n prefix such as `workOrders.status`; falls back to the raw value. */
  namespace: string;
  className?: string;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <Badge tone={toneFor(value)} className={className}>
      {t(`${namespace}.${value}`, { defaultValue: value.replaceAll("_", " ") })}
    </Badge>
  );
}

/** Page title row with optional actions. */
export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
}): ReactElement {
  return (
    <div className="mb-3 flex flex-wrap items-end justify-between gap-2.5 border-b pb-2.5">
      <div className="min-w-0 [overflow-wrap:anywhere]">
        <h1 className="text-lg font-semibold tracking-tight sm:text-xl">{title}</h1>
        {subtitle ? <p className="text-xs text-muted-foreground">{subtitle}</p> : null}
      </div>
      {actions ? (
        <div className="flex max-w-full flex-wrap items-center gap-1.5">{actions}</div>
      ) : null}
    </div>
  );
}

/** Centered spinner. */
export function LoadingState({ className }: { className?: string }): ReactElement {
  const { t } = useTranslation();
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn("flex items-center justify-center gap-2 p-8 text-muted-foreground", className)}
    >
      <Loader2 className="h-4 w-4 animate-spin" />
      <span>{t("app.loading")}</span>
    </div>
  );
}

/** Error panel with retry (invalidates all queries by default). */
export function ErrorState({
  error,
  onRetry,
}: {
  error: unknown;
  onRetry?: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const retry = onRetry ?? ((): void => void qc.invalidateQueries());
  return (
    <div
      role="alert"
      className="flex flex-col items-center gap-3 rounded-md border border-destructive/40 bg-destructive/5 p-8 text-center"
    >
      <AlertTriangle className="h-6 w-6 text-destructive" />
      <p className="text-sm">{errorMessage(error) || t("app.error")}</p>
      <Button variant="outline" size="sm" onClick={retry}>
        {t("app.retry")}
      </Button>
    </div>
  );
}

/** Empty list placeholder. */
export function EmptyState({ message }: { message?: string }): ReactElement {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center gap-2 rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
      <Inbox className="h-6 w-6" />
      <p>{message ?? t("app.empty")}</p>
    </div>
  );
}

/** Dashboard KPI tile; optional `to` makes it a link. */
export function KpiCard({
  label,
  value,
  hint,
  tone = "neutral",
  to,
  search,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: Tone;
  to?: string;
  search?: {
    status?: string;
    overdue?: boolean;
    open_only?: boolean;
    vessels_only?: boolean;
    below_min?: boolean;
    needs_review?: boolean;
    site_id?: string;
    tab?: string;
    chargeback_status?: string;
    due_before?: string;
    due_after?: string;
    completed_after?: string;
    completed_before?: string;
    ownership?: "owned" | "external";
  };
}): ReactElement {
  const toneClass: Record<Tone, string> = {
    neutral: "border-t-2 border-t-[#2d5275]",
    info: "border-t-2 border-t-[#2d5275]",
    success: "border-t-2 border-t-brand",
    warning: "border-t-2 border-t-amber-500",
    danger: "border-t-2 border-t-red-500",
    outline: "border-t-2 border-t-border",
  };
  const body = (
    <div
      className={cn(
        "min-w-0 rounded-md border bg-card p-2.5 [overflow-wrap:anywhere] sm:p-3",
        toneClass[tone],
      )}
    >
      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <p className="mt-1 text-xl font-bold tabular-nums">{value}</p>
      {hint ? <p className="mt-0.5 text-[11px] text-muted-foreground">{hint}</p> : null}
    </div>
  );
  return to ? (
    <Link
      to={to}
      search={search as never}
      className="block rounded-md focus-visible:outline-none focus-visible:ring-2"
    >
      {body}
    </Link>
  ) : (
    body
  );
}

/** Definition-list style key/value grid. */
export function Details({ items }: { items: { label: string; value: ReactNode }[] }): ReactElement {
  return (
    <dl className="grid grid-cols-1 gap-x-5 gap-y-2.5 sm:grid-cols-2 lg:grid-cols-3">
      {items.map((item) => (
        <div key={item.label} className="min-w-0 [overflow-wrap:anywhere]">
          <dt className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            {item.label}
          </dt>
          <dd className="text-xs">{item.value ?? "—"}</dd>
        </div>
      ))}
    </dl>
  );
}
